/* Fretboard explorer and live note detector ("Keşfet" tab). */
import { useRef, useState } from 'react';
import { audio } from '../../core/audio';
import { useMicFrame, useMicState } from '../../core/mic';
import { fretMidi, midiToFreq, mod, NATURALS, noteName, noteParts, positions, shortPc } from '../../core/theory';
import { fmtDec } from '../../core/util';
import { Btn } from '../../ui/Button';
import { Fretboard, type FretMark, pcMarks } from '../../ui/Fretboard';
import { Gauge, type GaugeHandle } from '../../ui/Gauge';
import { MicBlock } from '../exercise/MicBlock';
import { GroupTitle } from '../module/ModulePage';

function LiveDetector() {
  const gauge = useRef<GaugeHandle>(null);
  const [marks, setMarks] = useState<FretMark[]>([]);
  const last = useRef<{ m: number | null; cnt: number }>({ m: null, cnt: 0 });
  useMicFrame((f) => {
    if (!f.voiced || f.midi == null) {
      gauge.current?.update({ active: false });
      return;
    }
    const m = Math.round(f.midi);
    const c = (f.midi - m) * 100;
    const p = noteParts(m);
    gauge.current?.update({
      active: true,
      main: p.main,
      sub: p.sub || `${midiToFreq(m).toFixed(0)} Hz`,
      cents: c,
      hz: f.freq,
    });
    const st = last.current;
    if (m === st.m) st.cnt++;
    else {
      st.m = m;
      st.cnt = 0;
    }
    if (st.cnt === 3)
      setMarks(positions(m, 15).map((pp) => ({ s: pp.s, f: pp.f, cls: 'm-live', label: shortPc(mod(m, 12)) })));
  });
  return (
    <div className="live-box">
      <Gauge ref={gauge} />
      <Fretboard from={0} to={15} marks={marks} />
    </div>
  );
}

export function Explore() {
  const mic = useMicState();
  const [sel, setSel] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [info, setInfo] = useState('Bir perdeye dokun ya da aşağıdan bir nota seç.');
  let marks: FretMark[] = [];
  if (showAll) marks = pcMarks(NATURALS, { from: 0, to: 15 }).map((m) => ({ ...m, cls: 'm-note' }));
  if (sel != null)
    marks = marks
      .filter((m) => mod(fretMidi(m.s, m.f), 12) !== sel)
      .concat(pcMarks([sel], { from: 0, to: 15, root: sel }));
  return (
    <>
      <GroupTitle
        title="Sap haritası"
        sub="Bir notayı seçince sapta bulunduğu bütün yerler işaretlenir. Perdeye dokunarak sesini dinle."
      />
      <div className="explore">
        <Fretboard
          from={0}
          to={15}
          marks={marks}
          onPick={(sn, f, m) => {
            audio.resume();
            audio.playNote(m, { inst: 'guitar', dur: 1.2 });
            setInfo(`${sn}. tel, ${f}. perde: ${noteName(m)} · ${fmtDec(midiToFreq(m), 1)} Hz`);
          }}
        />
        <div className="live-note">{info}</div>
        <div className="ex-tools-row">
          <div className="seg">
            {Array.from({ length: 12 }, (_, pc) => (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: one button per pitch class
                key={pc}
                type="button"
                data-pc={pc}
                className={sel === pc ? 'on' : undefined}
                onClick={() => {
                  const next = sel === pc ? null : pc;
                  setSel(next);
                  if (next != null) audio.playNote(48 + pc, { inst: 'guitar', dur: 1 });
                }}
              >
                {shortPc(pc)}
              </button>
            ))}
          </div>
          <Btn kind={showAll ? 'soft' : 'ghost'} onClick={() => setShowAll(!showAll)}>
            Doğal notaları göster
          </Btn>
        </div>
      </div>
      <GroupTitle
        title="Canlı nota algılayıcı"
        sub="Gitarında bir nota çal: adını, yüksekliğini ve sapta çalınabildiği bütün yerleri gör."
      />
      {mic === 'on' ? (
        <LiveDetector />
      ) : (
        <MicBlock text="Mikrofonu açınca çaldığın notanın adını ve sapta nerelerde bulunduğunu canlı göreceksin." />
      )}
    </>
  );
}
